import { okhsl } from "../../../d3-mapp/color/index.js";
import { Signal, signal } from "../../../dsp/signal/signal.js";
import SignalHighLowPlot from "../../d3/signal/SignalHighLowPlot.svelte";
import SignalThresholdPlot from "../../d3/signal/SignalThresholdPlot.svelte";
import { LEGACY_DATA_COLOR_INTERPOLATOR } from "../../../d3-mapp/color/legacy-colors.js";
import { interpolateOkhsl } from "../../../d3-mapp/interpolate/interpolate-okhsl.js";
import { NoneCodec } from "../../../dsp/codecs/none-codec.js";
import { isq } from "../../../isq/index.js";
import { checkBounds } from "../../../dsp/signal/helpers/data-lookup.js";
const legacyOkhslDataColorRamp = LEGACY_DATA_COLOR_INTERPOLATOR(interpolateOkhsl);
// const extractMaximumsSpline = (
//   modellingRun: ModellingRunDefinition,
//   goiId: string
// ): Spline => {
//   const interval = modellingRun.interval * 60000;
//   const time = (index: number): number => modellingRun.profile[index].start;
//   const data = (index: number): SignalDatum =>
//     modellingRun.profile[index].analysis?.distribution[goiId]?.max;
//   return signal(time, data, interval).spline();
// };
const extractMaximumsSpline = (modellingRun, goiId, unit) => {
    const interval = modellingRun.interval * 60000;
    const time = (index) => { checkBounds(index, modellingRun.profile); return modellingRun.profile[index].start; };
    const data = (index) => {
        checkBounds(index, modellingRun.profile);
        return modellingRun.profile[index].analysis?.distribution[goiId]?.max;
    };
    return signal(time, data, interval, NoneCodec.get(), unit, unit);
};
const hasModellingRunResultsForGoi = (modellingRun, goiId) => modellingRun.profile.reduce((found, interval) => found || interval.analysis?.distribution[goiId] !== undefined, false);
// const extractMaximumsReceptorSplines = (
//   modellingRuns: ModellingRunDefinition[],
//   goiId: string
// ): Spline[] =>
//   modellingRuns.map((modellingRun: any) =>
//     extractMaximumsSpline(modellingRun, goiId)
//   );
const extractMaximumsReceptorSplines = (modellingRuns, goiId, unit) => modellingRuns.map((modellingRun) => extractMaximumsSpline(modellingRun, goiId, unit));
// const extractMaximumsModellingRunSplines = (
//   receptorIds: string[],
//   modellingRun: ModellingRunDefinition
// ): Spline[] =>
//   receptorIds.map((receptorId: any) =>
//     extractMaximumsSpline(modellingRun, receptorId)
//   );
const extractMaximumsModellingRunSplines = (receptorIds, modellingRun, unit) => receptorIds.map((receptorId) => extractMaximumsSpline(modellingRun, receptorId, unit));
const mapToUndefinedColor = (color) => color.darker();
const mapToSignalColor = (color) => color.brighter();
const mapToSignalStroke = (color) => ({
    color: mapToSignalColor(color),
    width: 1.5,
});
const createHighestMaximumsReceptorPlot = (sources, goId) => {
    let colors = sources.ids.map((sourceId) => sources.map[sourceId].color);
    let splineLabels = sources.ids.map((sourceId) => sources.map[sourceId].name);
    let unitSymbol = sources.map[sources.ids[0]].unit;
    let unit = sources.map[sources.ids[0]].isqUnit;
    let signalPlot = {
        type: SignalHighLowPlot,
        properties: {
            //      splines: extractMaximumsReceptorSplines(
            signals: extractMaximumsReceptorSplines(sources.ids.map((sourceId) => sources.map[sourceId].modellingRun), goId, unit),
            splineLabels,
            tags: [
                {
                    value: Number.NaN,
                    tag: "highest",
                    stroke: colors.map(mapToSignalStroke),
                    label: "Highest",
                },
            ],
            percentile: undefined,
            title: "Maximum concentration for all sources",
            yAxisLabel: `(${unitSymbol})`,
            colors,
            undefinedColors: colors.map(mapToUndefinedColor),
            scalarEther: false,
            showAxis: true,
            showThreshold: false,
            showProjection: true,
            showHistogram: true,
        },
    };
    return signalPlot;
};
const createMaximumsReceptorPlot = (source, goId, threshold, percentile, locale) => {
    let color = source.color;
    let splineLabel = `Maximum (${source.unit})`;
    let signalPlot = {
        type: SignalThresholdPlot,
        properties: {
            signals: [extractMaximumsSpline(source.modellingRun, goId, source.isqUnit)],
            splineLabels: [source.name],
            tags: [
                {
                    value: threshold,
                    //     tag: `${threshold.toFixed(1)}`,//`${threshold.toFixed(1)} ${modellingType.grid.unitLabel}`,
                    stroke: [mapToSignalStroke(color)],
                    label: `>= ${threshold.toLocaleString(locale)}`,
                },
            ],
            percentile,
            title: `Maximum concentration by ${source.name}`,
            yAxisLabel: `(${source.unit})`,
            colors: [color],
            undefinedColors: [mapToUndefinedColor(color)],
            scalarEther: false,
            showAxis: true,
            showThreshold: true,
            showProjection: true,
            showHistogram: true,
        },
    };
    return signalPlot;
};
export const createReceptorPlots = (sources, receptors, threshold, percentile, locale, maxGroupSize) => {
    if (sources && sources.ids.length > 0) {
        return receptors.ids.reduce((map, receptorId) => {
            const indexStart = sources.ids.length > 1 ? 1 : 0;
            map[receptorId] = (sources.ids.length > 1
                ? [[createHighestMaximumsReceptorPlot(sources, receptorId)]]
                : [[]]);
            sources.ids.reduce((plots, sourceId, sourceIndex) => {
                const plotIndex = (sourceIndex + indexStart) % maxGroupSize;
                const batchIndex = (sourceIndex + indexStart - plotIndex) / maxGroupSize;
                if (batchIndex === plots.length) {
                    plots.push([]);
                }
                plots[batchIndex].push(createMaximumsReceptorPlot(sources.map[sourceId], receptorId, threshold, percentile, locale));
                return plots;
            }, map[receptorId]);
            return map;
        }, {});
    }
    return undefined;
};
const createHighestMaximumsModellingRunPlot = (source, receptors) => {
    const usedReceptorIds = receptors.ids.filter((receptorId) => hasModellingRunResultsForGoi(source.modellingRun, receptorId));
    let colors = usedReceptorIds.map((rid) => receptors.map[rid].color);
    let splineLabels = usedReceptorIds.map((rid) => receptors.map[rid].name);
    let signalPlot = {
        type: SignalHighLowPlot,
        properties: {
            signals: extractMaximumsModellingRunSplines(usedReceptorIds, source.modellingRun, source.isqUnit),
            splineLabels,
            tags: [
                {
                    value: Number.NaN,
                    tag: "highest",
                    stroke: colors.map(mapToSignalStroke),
                    label: "Highest",
                },
            ],
            percentile: undefined,
            title: "Maximum concentration for all receptors",
            yAxisLabel: `(${source.unit})`,
            colors,
            undefinedColors: colors.map(mapToUndefinedColor),
            scalarEther: false,
            showAxis: true,
            showThreshold: false,
            showProjection: true,
            showHistogram: true,
        },
    };
    return signalPlot;
};
const createMaximumModellingRun = (source, receptor, threshold, percentile, locale) => {
    let color = receptor.color;
    // console.log(
    //   `createMaximumModellingRun source=${source.name} receptor=${receptor.name}`
    // );
    let signalPlot = {
        type: SignalThresholdPlot,
        properties: {
            signals: [extractMaximumsSpline(source.modellingRun, receptor.id, source.isqUnit)],
            splineLabels: [receptor.name],
            tags: [
                {
                    value: threshold,
                    //     tag: `${threshold.toFixed(1)}`,//`${threshold.toFixed(1)} ${modellingType.grid.unitLabel}`,
                    stroke: [mapToSignalStroke(color)],
                    label: `>= ${threshold.toLocaleString(locale)}`,
                },
            ],
            percentile,
            title: `Maximum concentration ${receptor.isHole ? "outside" : "inside"} ${receptor.name}`,
            yAxisLabel: `(${source.unit})`,
            colors: [color],
            undefinedColors: [mapToUndefinedColor(color)],
            scalarEther: false,
            showAxis: true,
            showThreshold: true,
            showProjection: true,
            showHistogram: true,
        },
    };
    return signalPlot;
};
export const createModellingRunPlots = (sources, receptors, threshold, percentile, locale, maxGroupSize) => {
    if (sources && sources.ids.length > 0) {
        return sources.ids
            .map((sourceId) => sources.map[sourceId])
            .reduce((map, source) => {
            const indexStart = receptors.ids.length > 1 ? 1 : 0;
            map[source.id] = (receptors.ids.length > 1
                ? [[createHighestMaximumsModellingRunPlot(source, receptors)]]
                : [[]]);
            receptors.ids
                .filter((receptorId) => hasModellingRunResultsForGoi(source.modellingRun, receptorId))
                .reduce((plots, receptorId, receptorIndex) => {
                const plotIndex = (receptorIndex + indexStart) % maxGroupSize;
                const batchIndex = (receptorIndex + indexStart - plotIndex) / maxGroupSize;
                if (batchIndex === plots.length) {
                    plots.push([]);
                }
                plots[batchIndex].push(createMaximumModellingRun(source, receptors.map[receptorId], threshold, percentile, locale));
                return plots;
            }, map[source.id]);
            return map;
        }, {});
    }
    return undefined;
};
export const extractReceptors = (gois, grid, useAutoColor = false) => {
    const receptorList = gois
        ? (gois.map((goi, i) => useAutoColor ? Object.assign({}, goi, { color: goi.autoColor }) : goi)).sort((r0, r1) => {
            const name0 = r0.name.toUpperCase();
            const name1 = r1.name.toUpperCase();
            if (name0 < name1) {
                return -1;
            }
            if (name0 > name1) {
                return 1;
            }
            return 0;
        })
        : [];
    if (grid) {
        receptorList.push(grid);
    }
    const ids = receptorList.map((receptor) => receptor.id);
    const receptors = receptorList.reduce((map, receptor) => {
        map[receptor.id] = receptor;
        return map;
    }, {});
    return {
        map: receptors,
        ids,
    };
};
export const extractSources = (modellingType, modellingRuns) => {
    const sourcesList = modellingRuns
        .map((modellingRun) => {
        return {
            id: modellingRun.id,
            name: modellingRun.parameters.description,
            source: modellingRun.source,
            color: okhsl(legacyOkhslDataColorRamp(modellingRun.parameters.render.hue / 360)),
            unit: modellingType.grid.unitLabel,
            isqUnit: isq.getUnit(modellingType.grid.unit),
            modellingRun,
        };
    })
        .sort((s0, s1) => {
        const name0 = s0.name.toUpperCase();
        const name1 = s1.name.toUpperCase();
        if (name0 < name1) {
            return -1;
        }
        if (name0 > name1) {
            return 1;
        }
        return 0;
    });
    const ids = sourcesList.map((source) => source.id);
    const sources = sourcesList.reduce((map, source) => {
        map[source.id] = source;
        return map;
    }, {});
    return {
        map: sources,
        ids,
    };
};
